import { useState, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_DOMAIN = "https://b1medicalsupply.com";

const PRESETS = [
  { label: "Storefront homepage", path: "/", tag: "Main store" },
  { label: "All products", path: "/products", tag: "Shop" },
  { label: "Diagnostics", path: "/products?category=Diagnostics", tag: "Category" },
  { label: "First Aid", path: "/products?category=First%20Aid", tag: "Category" },
  { label: "PPE", path: "/products?category=PPE", tag: "Category" },
  { label: "Mobility", path: "/products?category=Mobility", tag: "Category" },
  { label: "Wellness", path: "/products?category=Wellness", tag: "Category" },
];

function QRCard({ label, tag, url }) {
  const ref = useRef(null);
  const [copied, setCopied] = useState(false);

  const download = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `qr-${label.toLowerCase().replace(/\s+/g, "-")}.png`;
    a.click();
    toast.success("QR downloaded");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Link copied");
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col items-center text-center">
      <div className="text-[10px] uppercase tracking-wider text-[#638971] font-bold">{tag}</div>
      <div className="font-heading font-semibold text-[#1A1A1A] mt-1">{label}</div>
      <div ref={ref} className="mt-4 p-3 bg-white rounded-lg border border-gray-100">
        <QRCodeCanvas
          value={url}
          size={168}
          level="H"
          bgColor="#FFFFFF"
          fgColor="#2C4035"
          marginSize={2}
        />
      </div>
      <div className="mt-3 w-full text-[11px] font-mono text-[#595959] break-all bg-[#F6F5F2] rounded-lg px-2 py-1.5">
        {url}
      </div>
      <div className="mt-3 flex gap-2 w-full">
        <Button
          data-testid={`qr-download-${label.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={download}
          size="sm"
          className="flex-1 rounded-full bg-[#638971] hover:bg-[#4F705B] text-white"
        >
          <Download size={14} className="mr-1" /> PNG
        </Button>
        <Button
          data-testid={`qr-copy-${label.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={copyLink}
          size="sm"
          variant="outline"
          className="flex-1 rounded-full"
        >
          {copied ? <Check size={14} className="mr-1"/> : <Copy size={14} className="mr-1"/>}
          Link
        </Button>
      </div>
    </div>
  );
}

export default function ShareTab() {
  const [domain, setDomain] = useState(DEFAULT_DOMAIN);
  const [customPath, setCustomPath] = useState("/");
  const cleanDomain = domain.replace(/\/+$/, "");
  const customUrl = `${cleanDomain}${customPath.startsWith("/") ? customPath : "/" + customPath}`;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h2 className="font-heading text-xl font-bold text-[#1A1A1A]">Share your store</h2>
        <p className="text-sm text-[#595959] mt-1">
          Download QR codes to print on business cards, receipts, or share on social media.
          Scan with any phone camera to open your store.
        </p>
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#595959]">Your storefront URL</Label>
            <Input
              data-testid="qr-domain-input"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="https://b1medicalsupply.com"
              className="mt-1"
            />
            <div className="text-[11px] text-[#595959] mt-1">
              Change this to your custom domain (or use the preview URL) — all QR codes update instantly.
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PRESETS.map((p) => (
          <QRCard
            key={p.path}
            label={p.label}
            tag={p.tag}
            url={`${cleanDomain}${p.path}`}
          />
        ))}
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-5 flex flex-col items-center">
          <div className="text-[10px] uppercase tracking-wider text-[#E48967] font-bold">Custom link</div>
          <div className="font-heading font-semibold text-[#1A1A1A] mt-1">Any product or page</div>
          <div className="mt-4">
            <Input
              data-testid="qr-custom-path"
              value={customPath}
              onChange={(e) => setCustomPath(e.target.value)}
              placeholder="/products/PRODUCT_ID"
              className="text-xs font-mono"
            />
          </div>
          <div className="mt-4 p-3 bg-white rounded-lg border border-gray-100">
            <QRCodeCanvas
              value={customUrl}
              size={140}
              level="H"
              bgColor="#FFFFFF"
              fgColor="#2C4035"
              marginSize={2}
            />
          </div>
          <div className="mt-3 w-full text-[11px] font-mono text-[#595959] break-all bg-[#F6F5F2] rounded-lg px-2 py-1.5">
            {customUrl}
          </div>
        </div>
      </div>
    </div>
  );
}
